import {
  banner,
  boat,
  gear,
  identity,
  ops,
  projects,
  services,
  stack,
  whoami,
} from "./content";
import { currentTheme, setTheme, themeHelp } from "./theme";

const history: string[] = [];
let histIdx = -1;

function bullets(lines: string[]): string {
  return lines.map((l) => `  <span class="dim">»</span> ${l}`).join("\n");
}

function fieldRow(key: string, valHtml: string, keyW: number): string {
  return `  <span class="dim">»</span> ${esc(key.padEnd(keyW, " "))}  ${valHtml}`;
}

export function renderMotd(root: HTMLElement, started: number): void {
  const rows = services
    .map((s) => {
      const name = s.name.padEnd(8, " ");
      return `  <span class="dim">»</span> <a href="${s.href}">${esc(name)}</a> <span class="dim">${esc(s.host)}</span>\n      <span class="note">${esc(s.note)}</span>`;
    })
    .join("\n");

  const proj = projects
    .map((p) => {
      const name = p.href
        ? `<a href="${p.href}">${esc(p.name)}</a>`
        : `<span class="hi">${esc(p.name)}</span>`;
      return `  <span class="dim">»</span> ${name}\n      <span class="note">${esc(p.blurb)}</span>`;
    })
    .join("\n");

  const keyW = Math.max(...whoami.map((f) => f.key.length), "session".length);

  const who = whoami
    .map((f) => {
      const val = f.href
        ? `<a href="${f.href}">${esc(f.value)}</a>`
        : `<span class="hi">${esc(f.value)}</span>`;
      return fieldRow(f.key, val, keyW);
    })
    .join("\n");

  const stackBlock = bullets([
    `<span class="dim">langs</span>  <span class="hi">${esc(stack.langs)}</span>`,
    `<span class="dim">os</span>     <span class="hi">${esc(stack.os)}</span>`,
    `<span class="dim">tags</span>   <span class="hi">${esc(stack.tags)}</span>`,
  ]);

  const opsBlock = bullets(
    ops.map((o) => `<span class="note">${esc(o)}</span>`),
  );

  const gearBrief = bullets([
    `<span class="dim">desktop</span>  <span class="hi">Ryzen 7 5700X · RTX 4060 · 32 GB</span>`,
    `<span class="dim">desk</span>     <span class="hi">Type 68 Magnetic · Airox · Huion H430P</span>`,
    `<span class="dim">vr</span>       <span class="hi">Pico 4 Ultra 12/256</span>`,
    `<span class="dim">more</span>     <span class="note">type <kbd>gear</kbd> for full dump</span>`,
  ]);

  root.innerHTML = `
<pre class="banner chroma">${esc(banner)}</pre>
<p class="tagline">[ ${esc(identity.role)} ]</p>
<pre class="block">
<span class="sec">[ whoami ]</span>
${who}
${fieldRow("session", '<span id="uptime" class="hi">0s</span>', keyW)}
</pre>
<pre class="block">
<span class="sec">[ stack ]</span>
${stackBlock}
</pre>
<pre class="block">
<span class="sec">[ ops ]</span>
${opsBlock}
</pre>
<pre class="block">
<span class="sec">[ /net ]</span>
${rows}
</pre>
<pre class="block">
<span class="sec">[ projects ]</span>
${proj}
</pre>
<pre class="block">
<span class="sec">[ gear ]</span>
${gearBrief}
</pre>
<p class="hint">type <kbd>help</kbd> · <kbd>/color</kbd> · info updates over time · snapshot ${esc(identity.updated)}</p>
`.trim();

  const up = root.querySelector("#uptime");
  if (up) {
    const tick = () => {
      up.textContent = fmtUptime(Date.now() - started);
    };
    tick();
    window.setInterval(tick, 1000);
  }
}

export function attachShell(form: HTMLFormElement, input: HTMLInputElement, out: HTMLElement, scroll: HTMLElement): void {
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const raw = input.value;
    const line = raw.trim();
    input.value = "";
    if (!line) {
      print(out, promptLine(""), "");
      snap(scroll);
      return;
    }
    history.push(line);
    histIdx = history.length;
    print(out, promptLine(esc(line)), "");
    const result = run(line);
    if (result === "__CLEAR__") {
      out.replaceChildren();
    } else if (result) {
      print(out, result, "reply");
    }
    snap(scroll);
  });

  input.addEventListener("keydown", (e) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!history.length) return;
      histIdx = Math.max(0, histIdx - 1);
      input.value = history[histIdx] ?? "";
      input.setSelectionRange(input.value.length, input.value.length);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      histIdx = Math.min(history.length, histIdx + 1);
      input.value = history[histIdx] ?? "";
    } else if (e.key === "Tab") {
      e.preventDefault();
      const hit = complete(input.value);
      if (hit) input.value = hit;
    } else if (e.key === "l" && e.ctrlKey) {
      e.preventDefault();
      out.replaceChildren();
    } else if (e.key === "c" && e.ctrlKey) {
      e.preventDefault();
      print(out, `${promptLine(esc(input.value))}<span class="dim">^C</span>`, "");
      input.value = "";
      snap(scroll);
    }
  });
}

function run(line: string): string {
  const [cmdRaw, ...rest] = tokenize(line);
  const cmd = cmdRaw ?? "";
  const arg = rest.join(" ");
  switch (cmd) {
    case "help":
    case "?":
      return [
        "commands",
        "  help          this page",
        "  whoami        identity",
        "  stack         langs · os · focus",
        "  ops           devops · homelab highlights",
        "  gear          full hardware dump  (hw, specs)",
        "  neofetch      sysinfo-ish",
        "  ls [path]     motd sections",
        "  open <id>     osu | git | chat",
        "  /color [name] terminal palette  (green, amber, cyan, purple, rose)",
        "  ping          throw a ping into the water",
        "  boats         inspect the vessel",
        "  date          utc clock",
        "  clear         wipe scrollback  (ctrl-l)",
        "  contact       how to reach",
      ].join("\n");
    case "whoami":
      return whoami.map((f) => `${f.key.padEnd(12, " ")} ${f.value}`).join("\n");
    case "stack":
      return [
        `langs   ${stack.langs}`,
        `os      ${stack.os}`,
        `tags    ${stack.tags}`,
        `study   radio electronics · devices & circuits`,
      ].join("\n");
    case "ops":
      return ops.map((o, i) => `${String(i + 1).padStart(2, "0")}  ${o}`).join("\n");
    case "gear":
    case "hw":
    case "specs":
      return formatGear();
    case "neofetch":
      return [
        `${identity.handle}@${identity.host}`,
        "------------------------",
        `os       JVNKO TTY-01`,
        `shell    boats sh`,
        `term     crt / sonar · ${currentTheme()}`,
        `langs    ${identity.langs.join(", ")} · ${stack.langs}`,
        `study    radio electronics`,
        `uptime   this tab`,
        `net      ${services.length} public docks`,
        `gear     Ryzen 7 5700X · RTX 4060 · 32 GB`,
      ].join("\n");
    case "ls":
      return ls(arg);
    case "open":
    case "cd":
      return openSvc(arg || rest[0] || "");
    case "color":
      return arg ? setTheme(arg) : themeHelp();
    case "ping":
      return ping();
    case "boats":
    case "boat":
      return esc(boat).trimEnd() + "\n  tld .boats · vessel jvnko · no captain, just a helm";
    case "date":
      return new Date().toISOString().replace("T", " ").replace(/\.\d+Z$/, " UTC");
    case "clear":
      return "__CLEAR__";
    case "contact":
      return "see whoami for mail · no webmail dock on this tty";
    case "sudo":
      return "jvnko is already root on this glass tty.";
    case "uwu":
      return "noted. different harbor, same kind of night.";
    case "rm":
      return "rm: refusing to touch the ocean.";
    case "hack":
    case "hack the planet":
      return fakeHack();
    default:
      return `boats: ${cmd}: command not found\ntry help`;
  }
}

function formatGear(): string {
  const sec = (title: string, lines: string[]) =>
    [`[ ${title} ]`, ...lines.map((l) => `  ${l}`)].join("\n");
  return [
    sec("desktop", gear.desktop),
    sec("storage", gear.storage),
    sec("graveyard", gear.graveyard),
    sec("displays", gear.displays),
    sec("input", gear.input),
    sec("vr", gear.vr),
    sec("laptop", gear.laptop),
    sec("routers", gear.routers),
    sec("mobile", gear.mobile),
    `[ audio ]`,
    `  ${gear.audio}`,
    `[ telephony ]`,
    `  ${gear.telephony}`,
    sec("notes", gear.notes),
    "",
    `snapshot ${identity.updated} · specs drift over time`,
  ].join("\n");
}

function ls(path: string): string {
  const p = path.replace(/\/+$/, "") || ".";
  if (p === "." || p === "~" || p === "motd") {
    return "whoami  stack  ops  /net  projects  gear  (also: help, /color)";
  }
  if (p === "/net" || p === "net") {
    return services.map((s) => `${s.name.padEnd(8)} ${s.host}`).join("\n");
  }
  if (p === "projects" || p === "/projects") {
    return projects.map((x) => `${x.name.padEnd(14)} ${x.blurb}`).join("\n");
  }
  if (p === "stack") return `langs\nos\ntags`;
  if (p === "ops") return ops.join("\n");
  if (p === "gear" || p === "hw") return "desktop  storage  graveyard  input  vr  laptop  routers  mobile";
  return `ls: ${p}: no such file`;
}

function openSvc(id: string): string {
  if (id === "mail" || id.includes("mail.jvnko")) {
    return "open: mail dock is not advertised here";
  }
  const s = services.find((x) => x.id === id || x.name === id || x.host === id);
  if (!s) return `open: unknown dock '${id}'\ntry ls /net`;
  window.open(s.href, "_blank", "noopener,noreferrer");
  return `opening ${s.host}`;
}

function ping(): string {
  const ms = (Math.random() * 12 + 4).toFixed(2);
  return [
    "PING sonar.jvnko.boats (0.0.0.0): 56 data bytes",
    `64 bytes from helm: icmp_seq=0 ttl=64 time=${ms} ms`,
    "1 packets transmitted, 1 received",
  ].join("\n");
}

function fakeHack(): string {
  const bits = ["bios", "bus", "beatmaps", "matrix", "helm"];
  return bits.map((b, i) => `[${String(i + 1).padStart(2, "0")}] probing ${b} .............. ok`).join("\n") + "\naccess: you already live here.";
}

const CMDS = [
  "help", "whoami", "stack", "ops", "gear", "hw", "specs", "neofetch",
  "ls", "open", "color", "ping", "boats", "date", "clear", "contact",
];

function complete(cur: string): string | null {
  const t = cur.trim();
  if (!t) return null;
  if (t.startsWith("/")) {
    const inner = t.slice(1);
    if (!inner.includes(" ")) {
      const hits = CMDS.filter((c) => c.startsWith(inner));
      return hits.length === 1 ? `/${hits[0]}` : null;
    }
  }
  if (t.includes(" ")) {
    if (t.startsWith("open ") || t.startsWith("cd ") || t.startsWith("/open ")) {
      const q = t.split(/\s+/, 2)[1] ?? "";
      const hit = services.map((s) => s.id).find((id) => id.startsWith(q));
      const prefix = t.split(/\s+/, 1)[0];
      return hit ? `${prefix} ${hit}` : null;
    }
    if (t.startsWith("color ") || t.startsWith("/color ")) {
      const q = t.split(/\s+/, 2)[1] ?? "";
      const themes = ["green", "amber", "cyan", "purple", "rose"];
      const hit = themes.find((id) => id.startsWith(q));
      const prefix = t.split(/\s+/, 1)[0];
      return hit ? `${prefix} ${hit}` : null;
    }
    return null;
  }
  const bare = t.startsWith("/") ? t.slice(1) : t;
  const hits = CMDS.filter((c) => c.startsWith(bare));
  if (hits.length !== 1) return null;
  return t.startsWith("/") ? `/${hits[0]}` : hits[0];
}

function tokenize(line: string): string[] {
  const parts = line.trim().split(/\s+/);
  if (parts[0]?.startsWith("/")) parts[0] = parts[0].slice(1);
  return parts;
}

function promptLine(inner: string): string {
  return `<span class="who">jvnko</span><span class="at">@</span><span class="host">boats</span>:<span class="path">~</span><span class="hash">$</span> ${inner}`;
}

function print(out: HTMLElement, html: string, cls: string): void {
  const pre = document.createElement("pre");
  if (cls) pre.className = cls;
  pre.innerHTML = html;
  out.append(pre);
}

function snap(scroll: HTMLElement): void {
  scroll.scrollTop = scroll.scrollHeight;
}

function fmtUptime(ms: number): string {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${m}m ${sec}s`;
  if (m) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function esc(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}
